import { apiService } from "@/services/apiService";
import type { Submitter, SubmitterStatus } from "@repo/types";
import { Button } from "@repo/ui/components/Base/Button";
import { Spinner } from "@repo/ui/components/Base/Spinner";
import { useState } from "react";

type ResendState = "unset" | "loading" | "sent";

const pendingSubmitterStatuses: SubmitterStatus[] = [
  "pending",
  "sent",
  "opened",
];

const SubmitterRecord = ({
  tenantId,
  documentId,
  submitter,
}: {
  tenantId: string;
  documentId: string;
  submitter: Submitter;
}) => {
  const [resendState, setResendState] = useState<ResendState>("unset");

  return (
    <div className="flex flex-row gap-3 p-4 rounded-md odd:bg-neutral-100">
      <strong>Email:</strong> {submitter.email}
      {submitter.name ? (
        <>
          {" "}
          <strong>Name:</strong> {submitter.name}
        </>
      ) : null}{" "}
      <strong>Status: </strong>
      <span
        className={
          submitter.status === "completed"
            ? "text-green-600"
            : submitter.status === "pending" || submitter.status === "sent"
              ? "text-yellow-600"
              : submitter.status === "skipped"
                ? "text-gray-600"
                : "text-red-600"
        }
      >
        {submitter.status === "completed"
          ? "Completed"
          : submitter.status === "pending" || submitter.status === "sent"
            ? "Pending"
            : submitter.status === "skipped"
              ? "Skipped"
              : "Declined"}
      </span>{" "}
      {pendingSubmitterStatuses.includes(submitter.status) && (
        <Button
          variant="outline"
          size="xs"
          disabled={resendState !== "unset"}
          onClick={async () => {
            setResendState("loading");
            await apiService.v1.resendSignRequest(
              tenantId,
              documentId,
              submitter.id
            );
            setResendState("sent");
          }}
        >
          {resendState === "loading" && <Spinner className="w-5 h-5 mr-2" />}{" "}
          {resendState === "sent" ? "Sent" : "Re-send"}
        </Button>
      )}
    </div>
  );
};

export const SubmittersList = ({
  tenantId,
  documentId,
  submitters,
}: {
  tenantId: string;
  documentId: string;
  submitters: Submitter[] | undefined;
}) => {
  const waiting = submitters?.some(
    (submitter) =>
      submitter.status !== "completed" && submitter.status !== "declined"
  );

  return (
    <div className="flex flex-col gap-2">
      <p className="my-3 text-xl">
        {waiting
          ? "Waiting on response from signers:"
          : "All signers have responded:"}
      </p>

      <div className="flex flex-col gap-2">
        {submitters &&
          submitters.map((submitter) => (
            <SubmitterRecord
              key={submitter.id}
              submitter={submitter}
              tenantId={tenantId}
              documentId={documentId}
            />
          ))}
      </div>
    </div>
  );
};
